// md2.js – Quake 2 alias models (.md2, IDP2) and sprites (.sp2, IDS2).
// Skins and sprite frames are PCX pictures elsewhere in the pak; the loader
// resolves them.

import { cstr } from './pak.js';

// the 162 precomputed vertex normals (anorms.h), shared with Quake
export const ANORMS = anorms();

export class Md2 {
  constructor(buffer, name = '') {
    this.name = name;
    const dv = new DataView(buffer);
    const bytes = new Uint8Array(buffer);
    if (cstr(bytes, 0, 4) !== 'IDP2') throw new Error(`${name}: not an MD2`);
    const version = dv.getInt32(4, true);
    if (version !== 8) throw new Error(`${name}: MD2 version ${version}`);
    this.skinW = dv.getInt32(8, true);
    this.skinH = dv.getInt32(12, true);
    const frameSize = dv.getInt32(16, true);
    const numSkins = dv.getInt32(20, true);
    this.numVerts = dv.getInt32(24, true);
    this.numSt = dv.getInt32(28, true);
    this.numTris = dv.getInt32(32, true);
    this.numFrames = dv.getInt32(40, true);
    const ofsSkins = dv.getInt32(44, true), ofsSt = dv.getInt32(48, true), ofsTris = dv.getInt32(52, true), ofsFrames = dv.getInt32(56, true);
    this.skinNames = [];
    for (let i = 0; i < numSkins; i++) this.skinNames.push(cstr(bytes, ofsSkins + i * 64, 64).toLowerCase().replace(/\\/g, '/'));
    this.st = new Int16Array(this.numSt * 2);
    for (let i = 0; i < this.numSt; i++) {
      this.st[i * 2] = dv.getInt16(ofsSt + i * 4, true);
      this.st[i * 2 + 1] = dv.getInt16(ofsSt + i * 4 + 2, true);
    }
    // per triangle: 3 vertex indices then 3 texcoord indices
    this.tris = new Int16Array(this.numTris * 6);
    for (let i = 0; i < this.numTris; i++) for (let k = 0; k < 6; k++) this.tris[i * 6 + k] = dv.getInt16(ofsTris + i * 12 + k * 2, true);
    this.frames = [];
    let radius = 0;
    for (let i = 0; i < this.numFrames; i++) {
      const p = ofsFrames + i * frameSize;
      const scale = [dv.getFloat32(p, true), dv.getFloat32(p + 4, true), dv.getFloat32(p + 8, true)];
      const translate = [dv.getFloat32(p + 12, true), dv.getFloat32(p + 16, true), dv.getFloat32(p + 20, true)];
      const fname = cstr(bytes, p + 24, 16);
      const verts = bytes.subarray(p + 40, p + 40 + this.numVerts * 4);   // x y z normalIndex
      this.frames.push({ name: fname, scale, translate, verts });
      if (i === 0) {
        for (let k = 0; k < 3; k++) radius = Math.max(radius, Math.abs(translate[k]), Math.abs(translate[k] + scale[k] * 255));
      }
    }
    this.radius = radius;
    this.skins = [];   // filled by the loader: [{ w, h, data }]
    this.flags = 0;
  }

  /**
   * The frame runs. Quake 2 names frames "stand01".."stand40" or, when a
   * model has several sequences of a kind, "attak101".."attak112" and
   * "attak201".."attak208": the last two digits count within the run, so a
   * run with three or more trailing digits keeps its sequence digit
   * ("attak1", "attak2"); with two or fewer it is the bare name ("stand").
   */
  animations() {
    const out = [];
    for (let i = 0; i < this.frames.length; i++) {
      const base = animBase(this.frames[i].name);
      const last = out[out.length - 1];
      if (last && last.name === base) last.count++;
      else out.push({ name: base, first: i, count: 1 });
    }
    return out;
  }
}

export function animBase(name) {
  const m = /^(.*?)(\d+)$/.exec(name);
  if (!m) return name;
  return m[2].length >= 3 ? m[1] + m[2].slice(0, -2) : m[1];
}

export class Sp2 {
  constructor(buffer, name = '') {
    this.name = name;
    const dv = new DataView(buffer);
    const bytes = new Uint8Array(buffer);
    if (cstr(bytes, 0, 4) !== 'IDS2') throw new Error(`${name}: not a sprite`);
    const n = dv.getInt32(8, true);
    this.frames = [];
    let p = 12;
    let radius = 0;
    for (let i = 0; i < n; i++, p += 80) {
      const w = dv.getInt32(p, true), h = dv.getInt32(p + 4, true);
      const ox = dv.getInt32(p + 8, true), oy = dv.getInt32(p + 12, true);
      const pic = cstr(bytes, p + 16, 64).toLowerCase().replace(/\\/g, '/');
      // the poster's left edge is -origin_x to the right of the origin, its top (h - origin_y) up
      this.frames.push({ w, h, ox: -ox, oy: h - oy, pic, data: null });   // data filled by the loader
      radius = Math.max(radius, Math.hypot(w, h) / 2);
    }
    this.radius = radius;
  }
}

function anorms() {
  const a = [
    -0.525731, 0.000000, 0.850651, -0.442863, 0.238856, 0.864188, -0.295242, 0.000000, 0.955423, -0.309017, 0.500000, 0.809017,
    -0.162460, 0.262866, 0.951056, 0.000000, 0.000000, 1.000000, 0.000000, 0.850651, 0.525731, -0.147621, 0.716567, 0.681718,
    0.147621, 0.716567, 0.681718, 0.000000, 0.525731, 0.850651, 0.309017, 0.500000, 0.809017, 0.525731, 0.000000, 0.850651,
    0.295242, 0.000000, 0.955423, 0.442863, 0.238856, 0.864188, 0.162460, 0.262866, 0.951056, -0.681718, 0.147621, 0.716567,
    -0.809017, 0.309017, 0.500000, -0.587785, 0.425325, 0.688191, -0.850651, 0.525731, 0.000000, -0.864188, 0.442863, 0.238856,
    -0.716567, 0.681718, 0.147621, -0.688191, 0.587785, 0.425325, -0.500000, 0.809017, 0.309017, -0.238856, 0.864188, 0.442863,
    -0.425325, 0.688191, 0.587785, -0.716567, 0.681718, -0.147621, -0.500000, 0.809017, -0.309017, -0.525731, 0.850651, 0.000000,
    0.000000, 0.850651, -0.525731, -0.238856, 0.864188, -0.442863, 0.000000, 0.955423, -0.295242, -0.262866, 0.951056, -0.162460,
    0.000000, 1.000000, 0.000000, 0.000000, 0.955423, 0.295242, -0.262866, 0.951056, 0.162460, 0.238856, 0.864188, 0.442863,
    0.262866, 0.951056, 0.162460, 0.500000, 0.809017, 0.309017, 0.238856, 0.864188, -0.442863, 0.262866, 0.951056, -0.162460,
    0.500000, 0.809017, -0.309017, 0.850651, 0.525731, 0.000000, 0.716567, 0.681718, 0.147621, 0.716567, 0.681718, -0.147621,
    0.525731, 0.850651, 0.000000, 0.425325, 0.688191, 0.587785, 0.864188, 0.442863, 0.238856, 0.688191, 0.587785, 0.425325,
    0.809017, 0.309017, 0.500000, 0.681718, 0.147621, 0.716567, 0.587785, 0.425325, 0.688191, 0.955423, 0.295242, 0.000000,
    1.000000, 0.000000, 0.000000, 0.951056, 0.162460, 0.262866, 0.850651, -0.525731, 0.000000, 0.955423, -0.295242, 0.000000,
    0.864188, -0.442863, 0.238856, 0.951056, -0.162460, 0.262866, 0.809017, -0.309017, 0.500000, 0.681718, -0.147621, 0.716567,
    0.850651, 0.000000, 0.525731, 0.864188, 0.442863, -0.238856, 0.809017, 0.309017, -0.500000, 0.951056, 0.162460, -0.262866,
    0.525731, 0.000000, -0.850651, 0.681718, 0.147621, -0.716567, 0.681718, -0.147621, -0.716567, 0.850651, 0.000000, -0.525731,
    0.809017, -0.309017, -0.500000, 0.864188, -0.442863, -0.238856, 0.951056, -0.162460, -0.262866, 0.147621, 0.716567, -0.681718,
    0.309017, 0.500000, -0.809017, 0.425325, 0.688191, -0.587785, 0.442863, 0.238856, -0.864188, 0.587785, 0.425325, -0.688191,
    0.688191, 0.587785, -0.425325, -0.147621, 0.716567, -0.681718, -0.309017, 0.500000, -0.809017, 0.000000, 0.525731, -0.850651,
    -0.525731, 0.000000, -0.850651, -0.442863, 0.238856, -0.864188, -0.295242, 0.000000, -0.955423, -0.162460, 0.262866, -0.951056,
    0.000000, 0.000000, -1.000000, 0.295242, 0.000000, -0.955423, 0.162460, 0.262866, -0.951056, -0.442863, -0.238856, -0.864188,
    -0.309017, -0.500000, -0.809017, -0.162460, -0.262866, -0.951056, 0.000000, -0.850651, -0.525731, -0.147621, -0.716567, -0.681718,
    0.147621, -0.716567, -0.681718, 0.000000, -0.525731, -0.850651, 0.309017, -0.500000, -0.809017, 0.442863, -0.238856, -0.864188,
    0.162460, -0.262866, -0.951056, 0.238856, -0.864188, -0.442863, 0.500000, -0.809017, -0.309017, 0.425325, -0.688191, -0.587785,
    0.716567, -0.681718, -0.147621, 0.688191, -0.587785, -0.425325, 0.587785, -0.425325, -0.688191, 0.000000, -0.955423, -0.295242,
    0.000000, -1.000000, 0.000000, 0.262866, -0.951056, -0.162460, 0.000000, -0.850651, 0.525731, 0.000000, -0.955423, 0.295242,
    0.238856, -0.864188, 0.442863, 0.262866, -0.951056, 0.162460, 0.500000, -0.809017, 0.309017, 0.716567, -0.681718, 0.147621,
    0.525731, -0.850651, 0.000000, -0.238856, -0.864188, -0.442863, -0.500000, -0.809017, -0.309017, -0.262866, -0.951056, -0.162460,
    -0.850651, -0.525731, 0.000000, -0.716567, -0.681718, -0.147621, -0.716567, -0.681718, 0.147621, -0.525731, -0.850651, 0.000000,
    -0.500000, -0.809017, 0.309017, -0.238856, -0.864188, 0.442863, -0.262866, -0.951056, 0.162460, -0.864188, -0.442863, 0.238856,
    -0.809017, -0.309017, 0.500000, -0.688191, -0.587785, 0.425325, -0.681718, -0.147621, 0.716567, -0.442863, -0.238856, 0.864188,
    -0.587785, -0.425325, 0.688191, -0.309017, -0.500000, 0.809017, -0.147621, -0.716567, 0.681718, -0.425325, -0.688191, 0.587785,
    -0.162460, -0.262866, 0.951056, 0.442863, -0.238856, 0.864188, 0.162460, -0.262866, 0.951056, 0.309017, -0.500000, 0.809017,
    0.147621, -0.716567, 0.681718, 0.000000, -0.525731, 0.850651, 0.425325, -0.688191, 0.587785, 0.587785, -0.425325, 0.688191,
    0.688191, -0.587785, 0.425325, -0.955423, 0.295242, 0.000000, -0.951056, 0.162460, 0.262866, -1.000000, 0.000000, 0.000000,
    -0.850651, 0.000000, 0.525731, -0.955423, -0.295242, 0.000000, -0.951056, -0.162460, 0.262866, -0.864188, 0.442863, -0.238856,
    -0.951056, 0.162460, -0.262866, -0.809017, 0.309017, -0.500000, -0.864188, -0.442863, -0.238856, -0.951056, -0.162460, -0.262866,
    -0.809017, -0.309017, -0.500000, -0.681718, 0.147621, -0.716567, -0.681718, -0.147621, -0.716567, -0.850651, 0.000000, -0.525731,
    -0.688191, 0.587785, -0.425325, -0.587785, 0.425325, -0.688191, -0.425325, 0.688191, -0.587785, -0.425325, -0.688191, -0.587785,
    -0.587785, -0.425325, -0.688191, -0.688191, -0.587785, -0.425325,
  ];
  return new Float32Array(a);
}
